import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API object with all methods
const api = {
  // Raw HTTP methods for direct use
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  delete: (url, config) => apiClient.delete(url, config),
  
  // Expose defaults for baseURL access
  defaults: apiClient.defaults,

  // Auth
  login: (email, password) => 
    apiClient.post('/auth/login', { email, password }),
  
  getCurrentUser: () => 
    apiClient.get('/auth/me'),
  
  generateApiKey: () => 
    apiClient.post('/auth/api-key/generate'),

  // Agents
  getAgents: (params) => 
    apiClient.get('/agents', { params }),
  
  getAgent: (id) => 
    apiClient.get(`/agents/${id}`),
  
  createAgent: (data) => 
    apiClient.post('/agents', data),
  
  updateAgent: (id, data) => 
    apiClient.put(`/agents/${id}`, data),
  
  deleteAgent: (id) => 
    apiClient.delete(`/agents/${id}`),
  
  duplicateAgent: (id) =>
    apiClient.post(`/agents/${id}/duplicate`),

  // Conversational LLM catalog (providers, models, defaults).
  // Source of truth is api/src/config/llm-catalog.js — never hardcode model IDs
  // in the dashboard, or they go stale the next time a provider retires one.
  getLlmModels: (provider) =>
    apiClient.get('/agents/llm-models', { params: provider ? { provider } : undefined }),

  // Functions
  getFunctions: (agentId) => 
    apiClient.get(`/functions/agent/${agentId}`),
  
  createFunction: (agentId, data) => 
    apiClient.post(`/functions/agent/${agentId}`, data),
  
  updateFunction: (id, data) => 
    apiClient.put(`/functions/${id}`, data),
  
  deleteFunction: (id) => 
    apiClient.delete(`/functions/${id}`),
  
  testFunction: (functionId, testArguments) => 
    apiClient.post(`/functions/${functionId}/test`, { arguments: testArguments }),

  // Credits
  getCreditBalance: () => 
    apiClient.get('/credits/balance'),
  
  addCredits: (data) => 
    apiClient.post('/credits/add', data),
  
  getCreditTransactions: (params) => 
    apiClient.get('/credits/transactions', { params }),
  
  getCreditUsage: (days = 30) => 
    apiClient.get('/credits/usage', { params: { days } }),

  // Calls
  getCalls: (params) => 
    apiClient.get('/calls', { params }),
  
  getCall: (sessionId) => 
    apiClient.get(`/calls/${sessionId}`),
  
  getCallStats: (days) => 
    apiClient.get('/calls/stats/summary', { params: { days } }),
  
  getCallTranscript: (sessionId) =>
    apiClient.get(`/calls/${sessionId}/transcript`),

  // Realtime / Voice Testing
  getRealtimeToken: (agentId) => 
    apiClient.post('/realtime/token', { agent_id: agentId }),
  
  getDeepgramToken: (agentId) => 
    apiClient.post('/realtime/token/deepgram', { agent_id: agentId }),
  
  finalizeTestCall: (sessionId, durationMs) =>
    apiClient.post('/realtime/finalize', { 
      session_id: sessionId, 
      duration_ms: durationMs 
    }),

  // Live Monitor
  getActiveCalls: () =>
    apiClient.get('/monitor/calls'),
  
  getLiveTranscripts: () => api.get('/monitor/live-transcripts'),
  
  hangupCall: (sessionId) => api.post(`/monitor/calls/${sessionId}/hangup`),
  hangupAllCalls: () => api.post('/monitor/calls/hangup-all'),
  
  getMonitorStats: () =>
    apiClient.get('/monitor/stats'),
  
  getActiveCallDetails: (sessionId) =>
    apiClient.get(`/monitor/calls/${sessionId}`),

  // Users
  getUsers: (params) =>
    apiClient.get('/users', { params }),
  
  getUser: (id) =>
    apiClient.get(`/users/${id}`),
  
  createUser: (data) =>
    apiClient.post('/users', data),
  
  updateUser: (id, data) =>
    apiClient.put(`/users/${id}`, data),
  
  deleteUser: (id) =>
    apiClient.delete(`/users/${id}`),

  // IVR Configuration
  getIVRConfig: (agentId) =>
    apiClient.get(`/ivr/${agentId}/config`),
  
  updateIVRConfig: (agentId, data) =>
    apiClient.put(`/ivr/${agentId}/config`, data),
  
  getIntents: (agentId) =>
    apiClient.get(`/ivr/${agentId}/intents`),
  
  createIntent: (agentId, data) =>
    apiClient.post(`/ivr/${agentId}/intents`, data),
  
  updateIntent: (agentId, intentId, data) =>
    apiClient.put(`/ivr/${agentId}/intents/${intentId}`, data),
  
  deleteIntent: (agentId, intentId) =>
    apiClient.delete(`/ivr/${agentId}/intents/${intentId}`),

  // IVR Flows
  getFlows: (agentId) =>
    apiClient.get(`/flows/${agentId}`),
  
  getFlow: (agentId, flowId) =>
    apiClient.get(`/flows/${agentId}/${flowId}`),
  
  createFlow: (agentId, data) =>
    apiClient.post(`/flows/${agentId}`, data),
  
  updateFlow: (agentId, flowId, data) =>
    apiClient.put(`/flows/${agentId}/${flowId}`, data),
  
  deleteFlow: (agentId, flowId) =>
    apiClient.delete(`/flows/${agentId}/${flowId}`),

  // Languages
  getLanguages: () =>
    apiClient.get('/languages'),
  
  getAgentLanguages: (agentId) =>
    apiClient.get(`/languages/agent/${agentId}`),
  
  updateAgentLanguages: (agentId, data) =>
    apiClient.put(`/languages/agent/${agentId}`, data),

  // Audio Library
  getAudioFiles: (agentId) =>
    apiClient.get(`/ivr/${agentId}/audio`),
  
  uploadAudio: (agentId, formData) =>
    apiClient.post(`/ivr/${agentId}/audio/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  generateAudio: (agentId, data) =>
    apiClient.post(`/ivr/${agentId}/audio/generate`, data),
  
  deleteAudio: (agentId, audioId) =>
    apiClient.delete(`/ivr/${agentId}/audio/${audioId}`),
  
  getVoices: (agentId) =>
    apiClient.get(`/ivr/${agentId}/audio/voices`),

  // Analytics
  getAnalyticsOverview: (days = 30) =>
    apiClient.get('/analytics/overview', { params: { days } }),
  
  getCallChartData: (days = 30, agentId) =>
    apiClient.get('/analytics/calls/chart', { params: { days, agent_id: agentId } }),
  
  getHourlyCallData: (days = 7) =>
    apiClient.get('/analytics/calls/hourly', { params: { days } }),
  
  getAgentAnalytics: (days = 30) =>
    apiClient.get('/analytics/agents', { params: { days } }),
  
  getIntentAnalytics: (days = 30, agentId) =>
    apiClient.get('/analytics/intents', { params: { days, agent_id: agentId } }),
  
  getCostAnalytics: (days = 30) =>
    apiClient.get('/analytics/costs', { params: { days } }),
  
  getRealtimeMetrics: () =>
    apiClient.get('/analytics/realtime'),

  // AI Assist (optional)
  generateInstructions: (data) => 
    apiClient.post('/ai-assist/generate-instructions', data),
  
  getStrategyPresets: () => 
    apiClient.get('/ai-assist/strategy-presets'),
	
  // Gateways
  getGateways: (params) =>
    apiClient.get('/gateways', { params }),
  
  getGateway: (id) =>
    apiClient.get(`/gateways/${id}`),
  
  createGateway: (data) =>
    apiClient.post('/gateways', data),
  
  updateGateway: (id, data) =>
    apiClient.put(`/gateways/${id}`, data),
  
  deleteGateway: (id) =>
    apiClient.delete(`/gateways/${id}`),
  
  toggleGatewayStatus: (id) =>
    apiClient.patch(`/gateways/${id}/toggle-status`),
	
  // API Keys
  getApiKeys: () =>
    apiClient.get('/api-keys'),
  
  getApiKeysAudit: () =>
    apiClient.get('/api-keys/audit'),
  
  updateApiKeys: (data) =>
    apiClient.put('/api-keys', data),
  
  updateApiKey: (provider, key) =>
    apiClient.put(`/api-keys/${provider}`, { key }),
  
  deleteApiKey: (provider) =>
    apiClient.delete(`/api-keys/${provider}`),
  
  testApiKey: (provider, key) =>
    apiClient.post(`/api-keys/test/${provider}`, { key }),
  
  deleteAllCallLogs: () => api.delete('/calls/all'),

  // WhatsApp (OpenWA)
  getWaSessionStatus: () =>
    apiClient.get('/whatsapp/session/status'),

  getWaSessions: () =>
    apiClient.get('/whatsapp/sessions'),

  createWaSession: (name) =>
    apiClient.post('/whatsapp/sessions', { name }),

  startWaSession: (id) =>
    apiClient.post(`/whatsapp/sessions/${id}/start`),

  getWaSessionQr: (id) =>
    apiClient.get(`/whatsapp/sessions/${id}/qr`),

  deleteWaSession: (id) =>
    apiClient.delete(`/whatsapp/sessions/${id}`),

  getWaConversations: (params) =>
    apiClient.get('/whatsapp/conversations', { params }),

  getWaMessages: (conversationId, params) =>
    apiClient.get(`/whatsapp/conversations/${conversationId}/messages`, { params }),

  sendWaMessage: (conversationId, message) =>
    apiClient.post(`/whatsapp/conversations/${conversationId}/send`, { message }),

  markWaRead: (conversationId) =>
    apiClient.post(`/whatsapp/conversations/${conversationId}/read`),

  sendWaDirect: ({ to, message }) =>
    apiClient.post('/whatsapp/send', { to, message }),

};

import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:4000';

let socketInstance = null;

export function getSocket() {
  if (socketInstance?.connected) return socketInstance;

  const token = localStorage.getItem('token');
  socketInstance = io(SOCKET_URL, {
    auth: { token },
    transports: ['websocket'],
    reconnection: true,
    reconnectionDelay: 1000,
  });

  return socketInstance;
}

export function disconnectSocket() {
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
}

// Export both default and named exports for flexibility
export default api;
export { apiClient };
